import { useMemo, useState } from "react";
import { Alert, Form, Input, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { SECS, type EvidenceDto, type EvidenceTargetType } from "@audit-plan/shared";
import { uploadEvidence } from "../../api/evidence";
import { invalidateAudit, useAudit, useQuestions } from "../../api/audits";
import { useNotify } from "../audit/useNotify";
import { useFullModal } from "../audit/mobileModal";
import { EVIDENCE_ACCEPT, FilePicker } from "./FilePicker";
import { UploadHint, UploadQueueList } from "./UploadQueueList";
import { useUploadQueue } from "./useUploadQueue";

export { uploadEvidence };

export interface UploadCtx {
  supplierId: string;
  auditId?: string | null;
  target?: { type: EvidenceTargetType; id: string };
}

export { EVIDENCE_ACCEPT };

/** Etichetta leggibile del bersaglio di un'evidenza (usa audit e checklist in cache). */
export function useTargetLabel(auditId?: string | null) {
  const { t, i18n } = useTranslation();
  const audit = useAudit(auditId).data;
  const qs = useQuestions(audit?.checklistVersionId).data ?? [];
  return (tg?: { type: EvidenceTargetType; id: string } | null) => {
    if (!tg) return "—";
    const en = i18n.language === "en";
    switch (tg.type) {
      case "question":
        return `${t("question")} ${qs.find((q) => q.id === tg.id)?.code ?? ""}`.trim();
      case "nc":
        return `NC ${audit?.ncs.find((n) => n.id === tg.id)?.code ?? ""}`.trim();
      case "action":
        return `${en ? "Action" : "Azione"} ${audit?.actions.find((a) => a.id === tg.id)?.code ?? ""}`.trim();
      case "cap":
        return t("capDoc");
      case "supplier":
        return en ? "Supplier documents" : "Documentazione fornitore";
      default:
        return "Audit";
    }
  };
}

export function UploadModal(props: {
  open: boolean;
  onClose: () => void;
  ctx: UploadCtx;
  /** Se presente carica una nuova versione di questa evidenza. */
  evidenceId?: string;
  /** Estensione dell'evidenza esistente (nuova versione): il formato non puo' cambiare. */
  keepExt?: string;
  onDone?: (e: EvidenceDto) => void;
}) {
  const { open, onClose, ctx, evidenceId, onDone } = props;
  const { t, i18n } = useTranslation();
  const en = i18n.language === "en";
  const qc = useQueryClient();
  const n = useNotify();
  const [description, setDescription] = useState("");
  const [targetKey, setTargetKey] = useState<string>("");
  const [err, setErr] = useState("");
  const full = useFullModal();
  const queue = useUploadQueue({ multiple: !evidenceId });
  const { busy } = queue;

  const fixed = !!ctx.target && ctx.target.type !== "audit";
  const audit = useAudit(!fixed && ctx.auditId ? ctx.auditId : null).data;
  const qs = useQuestions(audit?.checklistVersionId).data ?? [];
  const options = useMemo(() => {
    if (!audit) return [];
    const o: { value: string; label: string }[] = [{ value: "audit|" + audit.id, label: en ? "Audit (general)" : "Audit (generale)" }];
    for (const q of qs.filter((x) => (SECS as readonly string[]).includes(x.section))) o.push({ value: "question|" + q.id, label: `${t("question")} ${q.code} - ${(en ? q.en || q.it : q.it).slice(0, 60)}` });
    for (const x of audit.ncs) o.push({ value: "nc|" + x.id, label: `NC ${x.code} - ${x.title.slice(0, 50)}` });
    for (const x of audit.actions) o.push({ value: "action|" + x.id, label: `${en ? "Action" : "Azione"} ${x.code}` });
    o.push({ value: "cap|" + audit.id, label: t("capDoc") });
    return o;
  }, [audit, qs, en, t]);

  const reset = () => {
    queue.reset();
    setDescription("");
    setTargetKey("");
    setErr("");
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!queue.items.length) return setErr(t("b_up_no_files"));
    setErr("");
    let target = ctx.target;
    if (!fixed) {
      if (targetKey) {
        const [type, id] = targetKey.split("|");
        target = { type: type as EvidenceTargetType, id: id! };
      } else if (ctx.auditId) target = { type: "audit", id: ctx.auditId };
      else target = { type: "supplier", id: ctx.supplierId };
    }
    // nuova versione: il formato deve restare quello dell'evidenza
    const r = await queue.upload({ supplierId: ctx.supplierId, auditId: ctx.auditId ?? null, target: target! }, description, evidenceId, props.keepExt);
    if (r.evidence.length) {
      invalidateAudit(qc, ctx.auditId ?? undefined);
      for (const ev of r.evidence) onDone?.(ev);
    }
    if (r.ok) {
      n.ok(t("saved"));
      close();
    } else setErr(t("b_up_some_failed"));
  };

  const failed = queue.items.some((x) => x.status === "error");
  const todo = queue.items.filter((x) => x.status !== "done").length;
  const title = evidenceId ? t("newVersion") : t("upload");

  return (
    <Modal
      {...full}
      open={open}
      onCancel={busy ? queue.cancel : close}
      onOk={submit}
      okText={failed && !busy ? t("b_up_retry") : todo > 1 ? t("b_up_start_n", { n: todo }) : evidenceId ? t("newVersion") : t("b_up_start")}
      cancelText={busy ? t("b_up_cancel") : t("cancel")}
      confirmLoading={busy}
      okButtonProps={{ disabled: !queue.items.length || todo === 0 }}
      closable={!busy}
      maskClosable={false}
      keyboard={!busy}
      title={title}
      destroyOnHidden
    >
      <Form layout="vertical">
        <FilePicker multiple={!evidenceId} disabled={busy} onFiles={(f) => (setErr(""), queue.add(f))} />
        <div style={{ marginTop: 6 }}>
          <UploadHint />
        </div>
        <UploadQueueList items={queue.items} busy={busy} onRemove={queue.remove} onRetry={submit} />
        {!fixed && !evidenceId && ctx.auditId ? (
          <Form.Item label={t("linkedTo")} style={{ marginTop: 16 }}>
            <Select options={options} value={targetKey || "audit|" + ctx.auditId} onChange={setTargetKey} showSearch optionFilterProp="label" loading={!audit} disabled={busy} />
          </Form.Item>
        ) : null}
        {!evidenceId ? (
          <Form.Item label={t("description")} style={{ marginTop: 16 }}>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} disabled={busy} />
          </Form.Item>
        ) : null}
        {busy ? <Alert type="info" showIcon message={t("b_up_uploading_n")} style={{ marginBottom: 8 }} /> : null}
        {err ? <Alert type="error" showIcon message={err} /> : null}
      </Form>
    </Modal>
  );
}
